import { notify } from '@/lib/toast';
import { useState, useCallback } from 'react';
import { RecordLink } from '@/components/records/RecordLink';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, PlayCircle, StopCircle, ArrowLeftRight, Eye, Printer, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth.store';
import { useLocationStore } from '@/stores/location.store';
import { formatCurrency, formatDate } from '@/lib/utils';
import api from '@/lib/api';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { ZReportView, type ZReportData } from '@/components/shifts/ZReportView';

interface ShiftRow {
  id: string;
  openedAt: string;
  closedAt?: string | null;
  openingCash: number | string;
  closingCash?: number | string | null;
  cashDiscrepancy?: number | string | null;
  status: string;
  user?: { firstName?: string; lastName?: string } | null;
  // POS-I93
  location?: { id: string; name: string } | null;
  registerName?: string | null;
  // POS-I192: true when the shift has a stored Z-Report
  hasZReport?: boolean;
}

/** POS-I93: "Main Store · Counter 2" (or "—"). */
const shiftPlace = (s: { location?: { name: string } | null; registerName?: string | null }) =>
  [s.location?.name, s.registerName].filter(Boolean).join(' · ') || '—';

/** T-481: read-only foreign cash received this shift (PKR expected cash already includes it). */
interface ForeignCashLine {
  currencyCode: string;
  foreignTotal: number;
  pkrEquivalent: number;
  saleCount: number;
}

interface VarianceLeaderboardRow {
  userId: string;
  name: string;
  shiftCount: number;
  totalVariance: number;
  totalAbsVariance: number;
  shortCount: number;
  overCount: number;
}

function formatDuration(start: string, end?: string) {
  const from = new Date(start);
  const to = end ? new Date(end) : new Date();
  const mins = Math.floor((to.getTime() - from.getTime()) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return `${d}d ${rh}h`;
  }
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function isShiftStale(openedAt: string): boolean {
  const opened = new Date(openedAt);
  const now = new Date();
  const openedDay = new Date(opened.getFullYear(), opened.getMonth(), opened.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return openedDay.getTime() < today.getTime();
}

interface DrawerEntry { id: string; type: string; amount: number; reason: string; createdAt: string }
interface ShiftReport {
  shift: ShiftRow & { expectedCash?: number | null };
  paymentBreakdown: Record<string, number>;
  saleCount: number;
  sales?: { id: string; invoiceNumber: string; totalAmount: number; createdAt: string; status: string }[];
  drawerEntries?: DrawerEntry[];
}
const DRAWER_LABEL: Record<string, string> = { DEPOSIT: 'Cash in', WITHDRAWAL: 'Cash out', OPENING: 'Opening', CLOSING: 'Closing' };
const isMoneyEntry = (e: DrawerEntry) => e.type === 'DEPOSIT' || e.type === 'WITHDRAWAL';

export function ShiftsPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission('shifts:manage') || hasPermission('shifts:*');

  const [page, setPage] = useState(1);
  const [shiftDateFrom, setShiftDateFrom] = useState('');
  const [shiftDateTo, setShiftDateTo] = useState('');
  const [openShiftOpen, setOpenShiftOpen] = useState(false);
  const [closeShiftOpen, setCloseShiftOpen] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [openNotes, setOpenNotes] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [varianceReason, setVarianceReason] = useState('');
  const [registerName, setRegisterName] = useState('');
  const { selectedLocationId, selectedLocationName } = useLocationStore();

  // POS-I88: cash in / cash out during the shift. POS-I91: shift detail.
  const [cashOpen, setCashOpen] = useState(false);
  const [cashForm, setCashForm] = useState({ type: 'WITHDRAWAL', amount: '', reason: '' });
  const [reportShiftId, setReportShiftId] = useState<string | null>(null);
  // POS-I192: the stored Z-Report dialog (opened by Z-Report buttons and right after a shift closes)
  const [zReportShiftId, setZReportShiftId] = useState<string | null>(null);
  const [closedMessage, setClosedMessage] = useState<string | null>(null);

  const qc = useQueryClient();

  // Real-time: refresh shifts when any shift event received
  const handleShiftUpdate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['shift-current'] });
    qc.invalidateQueries({ queryKey: ['shifts'] });
  }, [qc]);
  useSocketEvent('shift.updated', handleShiftUpdate);

  const { data: currentShift } = useQuery({
    queryKey: ['shift-current'],
    queryFn: () => api.get('/shifts/current').then(r => r.data),
    refetchInterval: 300_000, // 5 min safety-net; event-driven via WebSocket
  });

  const foreignCash: ForeignCashLine[] = currentShift?.foreignCash ?? [];
  // POS-I92: the store counts blind (no expected cash before the count).
  const blindCount: boolean = currentShift?.blindCount === true;

  const { data: shiftsData, isLoading: shiftsLoading } = useQuery<{ data?: ShiftRow[]; total?: number }>({
    queryKey: ['shifts', page, shiftDateFrom, shiftDateTo],
    queryFn: () => api.get('/shifts', {
      params: {
        page,
        limit: 15,
        ...(shiftDateFrom && { dateFrom: shiftDateFrom }),
        ...(shiftDateTo && { dateTo: shiftDateTo }),
      },
    }).then(r => r.data),
  });

  const { data: shiftReport, isLoading: reportLoading } = useQuery<ShiftReport>({
    queryKey: ['shift-report', reportShiftId],
    queryFn: () => api.get(`/shifts/${reportShiftId}/report`).then(r => r.data),
    enabled: !!reportShiftId,
  });

  const cashMutation = useMutation({
    mutationFn: () => api.post('/shifts/cash-drawer', { type: cashForm.type, amount: parseFloat(cashForm.amount), notes: cashForm.reason.trim() }),
    onSuccess: () => {
      toast.success(t('cashEntrySaved', { defaultValue: 'Cash entry saved' }));
      qc.invalidateQueries({ queryKey: ['shift-current'] });
      qc.invalidateQueries({ queryKey: ['current-shift'] });
      setCashOpen(false);
      setCashForm({ type: 'WITHDRAWAL', amount: '', reason: '' });
    },
    onError: (err) => notify.error(err, t('cashEntryFailed', { defaultValue: 'Failed to save the cash entry' })),
  });

  const openMutation = useMutation({
    // POS-I93: the drawer belongs to the location picked in the location bar and, optionally, a till.
    mutationFn: () => api.post('/shifts/open', {
      openingCash: parseFloat(openingCash),
      notes: openNotes || undefined,
      ...(selectedLocationId ? { locationId: selectedLocationId } : {}),
      ...(registerName.trim() ? { registerName: registerName.trim() } : {}),
    }),
    onSuccess: () => {
      toast.success(t('shiftOpened'));
      qc.invalidateQueries({ queryKey: ['shift-current'] });
      qc.invalidateQueries({ queryKey: ['shifts'] });
      setOpenShiftOpen(false);
      setOpeningCash('');
      setOpenNotes('');
      setRegisterName('');
    },
    onError: (err) => notify.error(err, t('failedToOpenShift')),
  });

  const { data: zReportRes, isLoading: zReportLoading } = useQuery<{ zReport: ZReportData | null; recordedBefore: boolean; closedAt?: string | null }>({
    queryKey: ['shift-z-report', zReportShiftId],
    queryFn: () => api.get(`/shifts/${zReportShiftId}/z-report`).then(r => r.data),
    enabled: !!zReportShiftId,
  });

  const closeMutation = useMutation({
    mutationFn: () => api.post('/shifts/close', { closingCash: parseFloat(closingCash), notes: closeNotes || undefined, varianceReason: varianceReason || undefined }),
    onSuccess: (res) => {
      // POS-I92: after a blind count the result is revealed only once the count is in.
      const closed = res?.data as { expectedCash?: number; closingCash?: number } | undefined;
      if (blindCount && closed?.expectedCash != null) {
        const diff = Number(closed.closingCash ?? 0) - Number(closed.expectedCash);
        toast.success(
          t('blindCountResult', {
            expected: formatCurrency(Number(closed.expectedCash)),
            result: Math.abs(diff) < 0.01 ? t('cashBalanced', { defaultValue: 'balanced' }) : `${formatCurrency(Math.abs(diff))} ${diff < 0 ? t('cashShort') : t('cashOver')}`,
            defaultValue: 'Shift closed. Expected cash {{expected}}: {{result}}',
          }),
          { duration: 12000 },
        );
      } else {
        toast.success(t('shiftClosed'));
      }
      qc.invalidateQueries({ queryKey: ['shift-current'] });
      qc.invalidateQueries({ queryKey: ['current-shift'] });
      qc.invalidateQueries({ queryKey: ['shifts'] });
      qc.invalidateQueries({ queryKey: ['shift-variance-leaderboard'] });
      qc.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      // POS-I192: offer the Z-Report of the shift that was just closed.
      const done = res?.data as { id?: string; cashDiscrepancy?: number } | undefined;
      if (done?.id) {
        const d = Number(done.cashDiscrepancy ?? 0);
        setClosedMessage(t('zrClosedVariance', { variance: `${d < 0 ? '-' : ''}${formatCurrency(Math.abs(d))}` }));
        setZReportShiftId(done.id);
      }
      setCloseShiftOpen(false);
      setClosingCash('');
      setCloseNotes('');
      setVarianceReason('');
    },
    onError: (err) => notify.error(err, t('failedToCloseShift')),
  });

  const { data: leaderboard = [] } = useQuery<VarianceLeaderboardRow[]>({
    queryKey: ['shift-variance-leaderboard'],
    queryFn: () => api.get('/shifts/variance-leaderboard', { params: { days: 30 } }).then(r => r.data),
    enabled: canManage,
  });

  const shifts: ShiftRow[] = shiftsData?.data ?? [];
  const total = shiftsData?.total ?? 0;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('shiftsTitle')}
        actions={canManage ? (
          <div className="flex gap-2">
            {!currentShift && (
              <Button onClick={() => setOpenShiftOpen(true)}>
                <PlayCircle className="h-4 w-4 me-2" /> {t('openShift')}
              </Button>
            )}
            {currentShift && (
              <Button variant="outline" onClick={() => setCashOpen(true)}>
                <ArrowLeftRight className="h-4 w-4 me-2" /> {t('cashInOut', { defaultValue: 'Cash in / out' })}
              </Button>
            )}
            {currentShift && (
              <Button variant="destructive" onClick={() => setCloseShiftOpen(true)}>
                <StopCircle className="h-4 w-4 me-2" /> {t('closeShift')}
              </Button>
            )}
          </div>
        ) : undefined}
      />

      {/* Active shift summary */}
      {currentShift && (
        <Card className={isShiftStale(currentShift.openedAt)
          ? 'border-warning bg-warning/10'
          : 'border-success bg-success/10'}>
          <CardHeader>
            <CardTitle className={`text-base flex items-center gap-2 ${isShiftStale(currentShift.openedAt)
              ? 'text-warning-text'
              : 'text-success-text'}`}>
              <PlayCircle className="h-5 w-5" /> {t('shiftActive')}
            </CardTitle>
            {isShiftStale(currentShift.openedAt) && (
              <p className="text-sm text-warning-text mt-1">{t('shiftStaleWarning')}</p>
            )}
          </CardHeader>
          <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">{t('started')}</p>
              <p className="font-medium">{formatDate(currentShift.openedAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t('duration')}</p>
              <p className="font-medium">{formatDuration(currentShift.openedAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t('colShiftPlace')}</p>
              <p className="font-medium">{shiftPlace(currentShift)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t('openingCash')}</p>
              <p className="font-medium">{formatCurrency(currentShift.openingCash)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t('liveSales')}</p>
              <p className="font-medium text-success-text">
                {blindCount ? t('blindCountHidden', { defaultValue: 'Hidden (blind count)' }) : formatCurrency(currentShift.liveSalesTotal ?? 0)}
              </p>
            </div>
            {((currentShift.drawerEntries ?? []) as DrawerEntry[]).filter(isMoneyEntry).length > 0 && (
              <div className="col-span-2 sm:col-span-4 border-t pt-3">
                <p className="text-muted-foreground text-xs mb-1">{t('cashInOut', { defaultValue: 'Cash in / out' })}</p>
                <ul className="space-y-1">
                  {((currentShift.drawerEntries ?? []) as DrawerEntry[]).filter(isMoneyEntry).map(e => (
                    <li key={e.id} className="flex justify-between gap-3">
                      <span>{formatDate(e.createdAt)} · {DRAWER_LABEL[e.type]} · {e.reason}</span>
                      <span className={e.type === 'DEPOSIT' ? 'text-success-text' : 'text-destructive-text'}>
                        {e.type === 'DEPOSIT' ? '+' : '-'}{formatCurrency(e.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Shifts history */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <CardTitle className="text-base">{t('shiftHistory')}</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">{t('from')}</span>
              <input
                type="date"
                value={shiftDateFrom}
                onChange={e => { setShiftDateFrom(e.target.value); setPage(1); }}
                className="rounded-md border bg-transparent px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-xs text-muted-foreground">{t('to')}</span>
              <input
                type="date"
                value={shiftDateTo}
                onChange={e => { setShiftDateTo(e.target.value); setPage(1); }}
                className="rounded-md border bg-transparent px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {(shiftDateFrom || shiftDateTo) && (
                <button
                  onClick={() => { setShiftDateFrom(''); setShiftDateTo(''); setPage(1); }}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  {t('clearDates')}
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {shiftsLoading ? <SkeletonTable rows={5} cols={5} /> : shifts.length === 0 ? (
            <EmptyState icon={Clock} title={t('noShiftsYet')} description={canManage ? t('openFirstShift') : ''} />
          ) : (
            <div className="overflow-x-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('colCashier')}</Th>
                    <Th>{t('colDate')}</Th>
                    <Th>{t('colShiftPlace')}</Th>
                    <Th>{t('duration')}</Th>
                    <Th className="text-end">{t('colOpening')}</Th>
                    <Th className="text-end">{t('colClosing')}</Th>
                    <Th className="text-end">{t('colDiscrepancy')}</Th>
                    <Th className="text-center">{t('colStatus')}</Th>
                    <Th><span className="sr-only">{t('details', { defaultValue: 'Details' })}</span></Th>
                  </Tr>
                </THead>
                <TBody>
                  {shifts.map((s) => {
                    const disc = Number(s.cashDiscrepancy ?? 0);
                    return (
                      <Tr key={s.id}>
                        <Td className="text-foreground" label={t('colCashier')}>{s.user?.firstName} {s.user?.lastName}</Td>
                        <Td className="text-muted-foreground text-xs" label={t('colDate')}>{formatDate(s.openedAt)}</Td>
                        <Td className="text-foreground text-xs" label={t('colShiftPlace')}>{shiftPlace(s)}</Td>
                        <Td className="text-foreground" label={t('duration')}>{formatDuration(s.openedAt, s.closedAt ?? undefined)}</Td>
                        <Td className="text-end text-foreground" label={t('colOpening')}>{formatCurrency(Number(s.openingCash))}</Td>
                        <Td className="text-end text-foreground" label={t('colClosing')}>{s.closingCash != null ? formatCurrency(Number(s.closingCash)) : '—'}</Td>
                        <Td label={t('colDiscrepancy')} className={`text-end font-medium ${disc < 0 ? 'text-destructive-text' : disc > 0 ? 'text-success-text' : ''}`}>
                          {s.cashDiscrepancy != null ? formatCurrency(Math.abs(disc)) + (disc < 0 ? ` ${t('cashShort')}` : disc > 0 ? ` ${t('cashOver')}` : '') : '—'}
                        </Td>
                        <Td className="text-center" label={t('colStatus')}>
                          {s.status === 'OPEN'
                            ? <span className="text-xs bg-success/10 text-success-text px-2 py-0.5 rounded-full">{t('statusOpen')}</span>
                            : <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{t('statusClosed')}</span>
                          }
                        </Td>
                        <Td label="">
                          <Button size="sm" variant="ghost" onClick={() => setReportShiftId(s.id)}>
                            <Eye className="h-4 w-4 me-1" /> {t('viewShift', { defaultValue: 'View' })}
                          </Button>
                          {s.status === 'CLOSED' && (
                            <Button size="sm" variant="ghost" onClick={() => { setClosedMessage(null); setZReportShiftId(s.id); }}>
                              <FileText className="h-4 w-4 me-1" /> {t('zrButton')}
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          )}
          <PaginationBar page={page} pageSize={15} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>

      {/* Cashier variance leaderboard (WF-19) */}
      {canManage && leaderboard.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('varianceLeaderboardTitle')}</CardTitle>
            <p className="text-xs text-muted-foreground">{t('varianceLeaderboardHint')}</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('colCashier')}</Th>
                    <Th className="text-end">{t('colShifts')}</Th>
                    <Th className="text-end">{t('colNetVariance')}</Th>
                    <Th className="text-end">{t('colTotalAbsVariance')}</Th>
                    <Th className="text-end">{t('colShortOver')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {leaderboard.map((u) => (
                    <Tr key={u.userId}>
                      <Td className="text-foreground" label={t('colCashier')}>{u.name}</Td>
                      <Td className="text-end text-foreground" label={t('colShifts')}>{u.shiftCount}</Td>
                      <Td label={t('colNetVariance')} className={`text-end font-medium ${u.totalVariance < 0 ? 'text-destructive-text' : u.totalVariance > 0 ? 'text-success-text' : ''}`}>{formatCurrency(Math.abs(u.totalVariance))}</Td>
                      <Td className="text-end text-foreground" label={t('colTotalAbsVariance')}>{formatCurrency(u.totalAbsVariance)}</Td>
                      <Td className="text-end text-muted-foreground text-xs" label={t('colShortOver')}>{u.shortCount} / {u.overCount}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Open Shift Dialog */}
      <Dialog header={t('openNewShift')} visible={openShiftOpen} onHide={() => setOpenShiftOpen(false)} className="w-full max-w-sm">
        <div className="space-y-4 pt-2">
          <div>
            <Label>{t('openingCashLabel')}</Label>
            <Input type="number" min="0" step="0.01" placeholder={t('cashInDrawerPlaceholder')} value={openingCash} onChange={e => setOpeningCash(e.target.value)} />
          </div>
          <div>
            <Label>{t('shiftLocation')}</Label>
            <p className="text-sm">{selectedLocationName || t('shiftNoLocation')}</p>
            <p className="text-xs text-muted-foreground">{t('shiftLocationHint')}</p>
          </div>
          <div>
            <Label>{t('registerNameLabel')}</Label>
            <Input maxLength={60} value={registerName} onChange={e => setRegisterName(e.target.value)} placeholder={t('registerNamePlaceholder')} />
          </div>
          <div>
            <Label>{t('notesOptional')}</Label>
            <Input value={openNotes} onChange={e => setOpenNotes(e.target.value)} />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={() => setOpenShiftOpen(false)}>{t('cancelBtn')}</Button>
            <Button
              onClick={() => {
                const cashAmount = parseFloat(openingCash);
                if (isNaN(cashAmount) || cashAmount < 0) {
                  toast.error(t('validOpeningCash'));
                  return;
                }
                openMutation.mutate();
              }}
              disabled={openingCash === '' || openMutation.isPending}
            >
              {openMutation.isPending ? t('openingShift') : t('openShift')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Cash in / out Dialog (POS-I88) */}
      <Dialog header={t('cashInOut', { defaultValue: 'Cash in / out' })} visible={cashOpen} onHide={() => setCashOpen(false)} className="w-full max-w-sm">
        <div className="space-y-4 pt-2">
          <div className="flex gap-2">
            {(['WITHDRAWAL', 'DEPOSIT'] as const).map(ty => (
              <Button key={ty} type="button" variant={cashForm.type === ty ? 'default' : 'outline'} className="flex-1"
                onClick={() => setCashForm(f => ({ ...f, type: ty }))}>
                {ty === 'WITHDRAWAL' ? t('cashOut', { defaultValue: 'Cash out (pay-out)' }) : t('cashIn', { defaultValue: 'Cash in (add float)' })}
              </Button>
            ))}
          </div>
          <div>
            <Label>{t('amountLabel', { defaultValue: 'Amount' })}</Label>
            <Input type="number" min="0.01" step="0.01" value={cashForm.amount} onChange={e => setCashForm(f => ({ ...f, amount: e.target.value }))} />
          </div>
          <div>
            <Label>{t('reasonLabel', { defaultValue: 'Reason' })}</Label>
            <Input value={cashForm.reason} onChange={e => setCashForm(f => ({ ...f, reason: e.target.value }))} placeholder={t('cashReasonPlaceholder', { defaultValue: 'e.g. paid the milk man' })} />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={() => setCashOpen(false)}>{t('cancelBtn')}</Button>
            <Button
              onClick={() => {
                if (!(parseFloat(cashForm.amount) > 0)) { toast.error(t('cashAmountRequired', { defaultValue: 'Enter an amount greater than 0' })); return; }
                if (!cashForm.reason.trim()) { toast.error(t('cashReasonRequired', { defaultValue: 'Enter a reason' })); return; }
                cashMutation.mutate();
              }}
              disabled={cashMutation.isPending}
            >
              {t('save', { defaultValue: 'Save' })}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Z-Report Dialog (POS-I192): the stored report of a closed shift, printable on 80 mm */}
      <Dialog
        header={t('zrTitle')}
        visible={!!zReportShiftId}
        onHide={() => { setZReportShiftId(null); setClosedMessage(null); }}
        className="z-report-dialog w-full max-w-sm"
      >
        {zReportLoading ? <SkeletonTable rows={6} cols={2} /> : (
          <div className="space-y-3">
            {closedMessage && <p className="z-report-actions rounded-md bg-success/10 p-2 text-sm font-medium text-success-text">{closedMessage}</p>}
            {zReportRes?.zReport ? (
              <ZReportView report={zReportRes.zReport} />
            ) : (
              <p className="rounded-md bg-muted p-3 text-sm">{t('zrNotRecorded')}</p>
            )}
            <div className="z-report-actions flex gap-2">
              {zReportRes?.zReport && (
                <Button className="flex-1 gap-2" onClick={() => window.print()}>
                  <Printer className="h-4 w-4" /> {t('zrPrint')}
                </Button>
              )}
              <Button variant="outline" className="flex-1" onClick={() => { setZReportShiftId(null); setClosedMessage(null); }}>{t('close', { defaultValue: 'Close' })}</Button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Shift detail Dialog (POS-I91) */}
      <Dialog header={t('shiftDetail', { defaultValue: 'Shift detail' })} visible={!!reportShiftId} onHide={() => setReportShiftId(null)} className="w-full max-w-2xl">
        {reportLoading || !shiftReport ? <SkeletonTable rows={4} cols={3} /> : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><p className="text-xs text-muted-foreground">{t('colShiftPlace')}</p><p className="font-medium">{shiftPlace(shiftReport.shift)}</p></div>
              <div><p className="text-xs text-muted-foreground">{t('colOpening')}</p><p className="font-medium">{formatCurrency(Number(shiftReport.shift.openingCash))}</p></div>
              <div><p className="text-xs text-muted-foreground">{t('expectedCashLabel')}</p><p className="font-medium">{shiftReport.shift.expectedCash != null ? formatCurrency(Number(shiftReport.shift.expectedCash)) : '—'}</p></div>
              <div><p className="text-xs text-muted-foreground">{t('colClosing')}</p><p className="font-medium">{shiftReport.shift.closingCash != null ? formatCurrency(Number(shiftReport.shift.closingCash)) : '—'}</p></div>
              <div><p className="text-xs text-muted-foreground">{t('colDiscrepancy')}</p><p className="font-medium">{shiftReport.shift.cashDiscrepancy != null ? formatCurrency(Number(shiftReport.shift.cashDiscrepancy)) : '—'}</p></div>
            </div>
            <div>
              <p className="font-medium mb-1">{t('paidBy', { defaultValue: 'Paid by' })}</p>
              <ul className="space-y-0.5">
                {Object.entries(shiftReport.paymentBreakdown).map(([m, v]) => (
                  <li key={m} className="flex justify-between"><span>{m.replace('_', ' ')}</span><span>{formatCurrency(v)}</span></li>
                ))}
              </ul>
            </div>
            {(shiftReport.drawerEntries ?? []).filter(isMoneyEntry).length > 0 && (
              <div>
                <p className="font-medium mb-1">{t('cashInOut', { defaultValue: 'Cash in / out' })}</p>
                <ul className="space-y-0.5">
                  {(shiftReport.drawerEntries ?? []).filter(isMoneyEntry).map(e => (
                    <li key={e.id} className="flex justify-between gap-3"><span>{formatDate(e.createdAt)} · {DRAWER_LABEL[e.type]} · {e.reason}</span><span>{e.type === 'DEPOSIT' ? '+' : '-'}{formatCurrency(e.amount)}</span></li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <p className="font-medium mb-1">{t('salesInShift', { defaultValue: 'Sales in this shift' })} ({shiftReport.saleCount})</p>
              <ul className="max-h-60 overflow-y-auto space-y-0.5">
                {(shiftReport.sales ?? []).map(s => (
                  <li key={s.id} className="flex justify-between gap-3"><span className="text-xs"><RecordLink type="invoice" id={s.id} label={s.invoiceNumber} variant="mono" className="text-xs" /> · {formatDate(s.createdAt)}</span><span>{formatCurrency(s.totalAmount)}</span></li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Dialog>

      {/* Close Shift Dialog */}
      <Dialog header={t('closeShift')} visible={closeShiftOpen} onHide={() => setCloseShiftOpen(false)} className="w-full max-w-sm">
        <div className="space-y-4 pt-2">
          {currentShift && blindCount && (
            <div className="text-sm text-muted-foreground bg-muted rounded-md p-3">
              {t('blindCountHint', { defaultValue: 'Blind count: count every note and coin in the drawer and enter the total. The expected cash is shown after the shift is closed.' })}
            </div>
          )}
          {currentShift && !blindCount && (
            <div className="text-sm text-muted-foreground bg-muted rounded-md p-3">
              {t('expectedCashLabel')}: <strong>{formatCurrency(currentShift.expectedCash ?? ((currentShift.openingCash ?? 0) + (currentShift.liveCashTotal ?? 0)))}</strong>
              {Number(currentShift.liveCustomerCash ?? 0) > 0 && (
                <p className="mt-1 text-xs">{t('khataCashIncluded', { amount: formatCurrency(Number(currentShift.liveCustomerCash)) })}</p>
              )}
            </div>
          )}
          {foreignCash.length > 0 && (
            <div className="rounded-md border border-border p-3 text-sm">
              <p className="font-medium">{t('foreignCashReceived')}</p>
              <p className="mb-2 text-xs text-muted-foreground">{t('foreignCashHint')}</p>
              <ul className="space-y-1">
                {foreignCash.map((line) => (
                  <li key={line.currencyCode} className="flex items-center justify-between gap-3">
                    <span className="font-mono font-semibold">
                      {line.currencyCode} {line.foreignTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('foreignCashPkr', { amount: formatCurrency(line.pkrEquivalent), count: line.saleCount })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <Label>{t('closingCashLabel')}</Label>
            <Input type="number" min="0" step="0.01" placeholder={t('countCashPlaceholder')} value={closingCash} onChange={e => setClosingCash(e.target.value)} />
          </div>
          {!blindCount && (() => {
            const expected = currentShift?.expectedCash ?? ((currentShift?.openingCash ?? 0) + (currentShift?.liveCashTotal ?? 0));
            const counted = parseFloat(closingCash);
            if (isNaN(counted)) return null;
            const variance = counted - expected;
            if (Math.abs(variance) < 0.01) return null;
            return (
              <div className={`text-sm rounded-md p-3 ${variance < 0 ? 'bg-destructive/10 text-destructive-text' : 'bg-warning/10 text-warning-text'}`}>
                {variance < 0 ? t('cashShort') : t('cashOver')}: <strong>{formatCurrency(Math.abs(variance))}</strong>
              </div>
            );
          })()}
          <div>
            <Label>{t('varianceReasonLabel')}</Label>
            <Input value={varianceReason} onChange={e => setVarianceReason(e.target.value)} placeholder={t('varianceReasonPlaceholder')} />
            <p className="text-xs text-muted-foreground mt-1">{t('varianceReasonHint')}</p>
          </div>
          <div>
            <Label>{t('notesOptional')}</Label>
            <Input value={closeNotes} onChange={e => setCloseNotes(e.target.value)} />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={() => setCloseShiftOpen(false)}>{t('cancelBtn')}</Button>
            <Button
              variant="destructive"
              onClick={() => {
                const cashAmount = parseFloat(closingCash);
                if (isNaN(cashAmount) || cashAmount < 0) {
                  toast.error(t('validClosingCash'));
                  return;
                }
                closeMutation.mutate();
              }}
              disabled={closingCash === '' || closeMutation.isPending}
            >
              {closeMutation.isPending ? t('closingShift') : t('closeShift')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
